import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import "pages/AdminPage.scss";

interface Coupon {
  id: string;
  code: string;
  name: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  minOrderAmount: number;
  usedCount: number;
  totalCount: number;
  status: "active" | "expired" | "scheduled";
  validFrom: string;
  validTo: string;
}

const MOCK: Coupon[] = [
  {
    id: "1",
    code: "WEDDING2024",
    name: "신혼 할인 쿠폰",
    discountType: "percent",
    discountValue: 10,
    minOrderAmount: 50000,
    usedCount: 142,
    totalCount: 500,
    status: "active",
    validFrom: "2024-01-01",
    validTo: "2024-12-31",
  },
  {
    id: "2",
    code: "SPRING2024",
    name: "봄 시즌 쿠폰",
    discountType: "percent",
    discountValue: 15,
    minOrderAmount: 100000,
    usedCount: 88,
    totalCount: 200,
    status: "active",
    validFrom: "2024-03-01",
    validTo: "2024-05-31",
  },
  {
    id: "3",
    code: "NEWUSER",
    name: "신규 가입 쿠폰",
    discountType: "fixed",
    discountValue: 5000,
    minOrderAmount: 30000,
    usedCount: 1024,
    totalCount: 9999,
    status: "active",
    validFrom: "2024-01-01",
    validTo: "2024-12-31",
  },
  {
    id: "4",
    code: "WINTER2023",
    name: "겨울 시즌 쿠폰",
    discountType: "percent",
    discountValue: 20,
    minOrderAmount: 80000,
    usedCount: 356,
    totalCount: 400,
    status: "expired",
    validFrom: "2023-12-01",
    validTo: "2024-02-28",
  },
  {
    id: "5",
    code: "SUMMER2024",
    name: "여름 시즌 쿠폰",
    discountType: "percent",
    discountValue: 12,
    minOrderAmount: 60000,
    usedCount: 0,
    totalCount: 300,
    status: "scheduled",
    validFrom: "2024-06-01",
    validTo: "2024-08-31",
  },
];

const STATUS_BADGE: Record<string, "active" | "draft" | "info"> = { active: "active", expired: "draft", scheduled: "info" };
const STATUS_LABEL: Record<string, string> = {
  active: "진행중",
  expired: "만료됨",
  scheduled: "예정",
};

const TABS: TabItem[] = [
  { key: "all", label: "전체", count: 5 },
  { key: "active", label: "진행중", count: 3 },
  { key: "scheduled", label: "예정", count: 1 },
  { key: "expired", label: "만료됨", count: 1 },
];

const CouponManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);

  const filtered = MOCK.filter((c) => activeTab === "all" || c.status === activeTab);

  const columns: Column<Coupon>[] = [
    {
      key: "code",
      title: "쿠폰 코드",
      width: "140px",
      render: (v) => <code style={{ background: "#F4F4F5", padding: "2px 6px", borderRadius: 4, fontSize: 12 }}>{v}</code>,
    },
    { key: "name", title: "쿠폰명" },
    {
      key: "discountValue",
      title: "할인",
      width: "90px",
      render: (v, row) => (row.discountType === "percent" ? `${v}%` : `${v.toLocaleString()}원`),
    },
    {
      key: "minOrderAmount",
      title: "최소 주문",
      width: "100px",
      render: (v) => `${v.toLocaleString()}원`,
    },
    {
      key: "usedCount",
      title: "사용/발급",
      width: "100px",
      render: (v, row) => `${v} / ${row.totalCount === 9999 ? "무제한" : row.totalCount}`,
    },
    {
      key: "validFrom",
      title: "유효기간",
      width: "200px",
      render: (v, row) => `${v} ~ ${row.validTo}`,
    },
    {
      key: "status",
      title: "상태",
      width: "90px",
      render: (v) => <Badge variant={STATUS_BADGE[v]}>{STATUS_LABEL[v]}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "80px",
      render: () => (
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost">
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="쿠폰 관리"
        description="할인 쿠폰을 생성하고 관리합니다."
        actions={[
          <Button key="add" variant="primary" onClick={() => setShowModal(true)}>
            + 쿠폰 생성
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />
        <Table columns={columns} data={filtered} rowKey="id" />
        <Pagination current={currentPage} total={filtered.length} onChange={setCurrentPage} />
      </div>

      <Modal
        open={showModal}
        title="쿠폰 생성"
        onClose={() => setShowModal(false)}
        width="480px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              생성
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input label="쿠폰 코드" placeholder="예: WEDDING2024" />
          <Input label="쿠폰명" placeholder="쿠폰 이름 입력" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select
              label="할인 유형"
              options={[
                { value: "percent", label: "퍼센트 (%)" },
                { value: "fixed", label: "정액 (원)" },
              ]}
            />
            <Input label="할인 값" placeholder="10" type="number" />
          </div>
          <Input label="최소 주문 금액 (원)" placeholder="50000" type="number" />
          <Input label="발급 수량" placeholder="무제한은 빈칸" type="number" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input label="시작일" type="date" />
            <Input label="종료일" type="date" />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CouponManagement;
